// Mai Shayi — product detail behaviour: gallery, quantity stepper, and
// option -> variant resolution.
//
// Adding to cart is NOT handled here. The buy form is Horizon's own
// <product-form-component>, which owns the request, the cart drawer, cart
// section rendering and the cart events. This script only writes into that
// form's data (the hidden variant id and the quantity input) and keeps the
// price/availability in sync, so there is exactly one add-to-cart path.
(() => {
  /** Replace only the button's own label text node, leaving the theme's icon
   *  and checkmark-burst markup intact. */
  const setButtonLabel = (btn, text) => {
    const node = Array.from(btn.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
    if (node) node.textContent = text;
  };

  const init = (root) => {
    if (!root || typeof root.querySelector !== 'function') return;

    // --- gallery ---------------------------------------------------------
    const gallery = root.querySelector('[data-ms-gallery]');
    if (gallery && !gallery.dataset.msReady) {
      gallery.dataset.msReady = '1';
      const stage = gallery.querySelector('[data-ms-gallery-stage]');
      gallery.querySelectorAll('[data-ms-gallery-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (stage && thumb.dataset.full) {
            stage.src = thumb.dataset.full;
            stage.removeAttribute('srcset');
          }
          gallery
            .querySelectorAll('[data-ms-gallery-thumb]')
            .forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
        });
      });
    }

    // --- quantity --------------------------------------------------------
    root.querySelectorAll('.ms-pdp__qty').forEach((box) => {
      if (box.dataset.msReady) return;
      box.dataset.msReady = '1';
      const input = box.querySelector('input');
      box.querySelectorAll('[data-ms-qty]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const min = parseInt(input.min, 10) || 1;
          const next = (parseInt(input.value, 10) || min) + parseInt(btn.dataset.msQty, 10);
          input.value = Math.max(min, next);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });

    // --- options ---------------------------------------------------------
    const form = root.querySelector('#ms-pdp-form');
    const variants = window.msPdpVariants;
    if (!form || !Array.isArray(variants) || form.dataset.msReady) return;
    form.dataset.msReady = '1';

    const idField = form.querySelector('[data-ms-variant-id]');
    const buy = form.querySelector('[data-ms-buy]');
    const addBtn = form.querySelector('.ms-pdp__add');
    const priceEl = (form.closest('.ms-pdp') || document).querySelector('[data-ms-price]');
    const addLabel = addBtn ? (Array.from(addBtn.childNodes).find(
      (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim()
    )?.textContent.trim() || 'Add to bag') : '';
    const soldLabel = buy?.dataset.msSold || 'Sold out';

    const chosen = () =>
      Array.from(form.querySelectorAll('.ms-pdp__option')).reduce((acc, set) => {
        const on = set.querySelector('.ms-pdp__swatch[aria-pressed="true"]');
        if (on) acc.push(on.dataset.value);
        return acc;
      }, []);

    const sync = () => {
      const want = chosen();
      if (!want.length) return;
      const match = variants.find((variant) => want.every((value, i) => variant.options[i] === value));
      if (!match) return;

      if (idField) idField.value = match.id;
      if (priceEl && match.price_formatted) priceEl.textContent = match.price_formatted;
      if (addBtn) {
        // product-form-component refuses to submit while the button is disabled,
        // which is exactly the behaviour we want for an unavailable variant.
        addBtn.disabled = !match.available;
        setButtonLabel(addBtn, match.available ? addLabel : soldLabel);
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    };

    form.querySelectorAll('.ms-pdp__swatch').forEach((btn) => {
      btn.addEventListener('click', () => {
        btn.parentElement
          .querySelectorAll('.ms-pdp__swatch')
          .forEach((sibling) => sibling.setAttribute('aria-pressed', String(sibling === btn)));
        sync();
      });
    });
  };

  const scan = () => init(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
